import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import type { FC } from "react";

import { DateTimePickerModal } from "@pianofunclub/react-native-paper-dates";
import type { NavigationAction } from "@react-navigation/native";
import { endOfWeek, startOfWeek } from "date-fns";
import { Box, Heading, HStack, useToast } from "native-base";
import { useWindowDimensions } from "react-native";
import { useQueryLoader, useMutation } from "react-relay";

import type {
  TeacherTimetableStackNavigatorProps,
  TeacherTimetableStackRouteProps,
} from "pianofunclub-crm/navigation/TeacherTimetableNavigator";

import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import { TimetableIcon } from "pianofunclub-shared/components/Other/Icons";
import TeacherTimetable from "pianofunclub-shared/components/Registers/TeacherTimetable";
import type { TeacherTimetableReducerValues } from "pianofunclub-shared/components/Registers/TeacherTimetable";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { LoadLessonsQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadLessonsQuery.graphql";
import type {
  RescheduleLessonMutation,
  RescheduleLessonMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/RescheduleLessonMutation.graphql";
import { load_lessons } from "pianofunclub-shared/relay/graphql/registers/LoadLessons";
import { reschedule_lesson } from "pianofunclub-shared/relay/graphql/registers/RescheduleLesson";
import { customColors } from "pianofunclub-shared/theme";
import { getScaledWindowDimension } from "pianofunclub-shared/utils/converters";
import {
  getCurrentBlock,
  getDefaultStartingYearAndStartingYearsForSelect,
} from "pianofunclub-shared/utils/extractors";
import { createReducer } from "pianofunclub-shared/utils/reducers";

export type NavigationProps = TeacherTimetableStackNavigatorProps<"TeacherTimetableHub">;

type RouteProps = TeacherTimetableStackRouteProps<"TeacherTimetableHub">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

export interface ReducerValues extends TeacherTimetableReducerValues {
  leaveAlertAction?: NavigationAction | (() => void);
  school?: string;
}

const TeacherTimetableHubScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const { user } = useAuth();

  const defaultStartingYear = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect()[0],
    []
  );

  const initialState = useMemo(() => {
    let initialStartingYear = route.params?.startingYear;
    if (initialStartingYear === undefined) {
      initialStartingYear = user?.profile?.profileGroup?.currentStartingYear ?? defaultStartingYear;
      navigation.setParams({
        startingYear: initialStartingYear,
      });
    }
    let initialBlock = route.params?.block;
    if (initialBlock === undefined) {
      if (
        user?.profile?.profileGroup?.currentStartingYear &&
        user?.profile?.profileGroup?.currentStartingYear > defaultStartingYear
      ) {
        initialBlock = 1;
      } else {
        initialBlock = user?.profile?.currentBlock ?? getCurrentBlock();
      }
      navigation.setParams({
        block: initialBlock,
      });
    }
    return {
      values: {
        block: Number(initialBlock),
        contentIsRendered: false,
        dayIndex: 0,
        isRefetching: false,
        schoolId: route.params?.schoolId,
        searchTerm: route.params?.searchTerm ?? "",
        startingYear: Number(initialStartingYear),
        teachingDays: [],
        timetableDate: new Date(),
        timetableView: "WEEK" as const,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadLessonsQueryReference, loadLessonsQuery] =
    useQueryLoader<LoadLessonsQuery>(load_lessons);

  useEffect(() => {
    if (user?.profile?.id) {
      let startDate = state.values.timetableDate ?? new Date();
      let endDate;
      if (state.values.timetableView === "WEEK") {
        startDate = startOfWeek(startDate, { weekStartsOn: 1 });
        endDate = endOfWeek(startDate, { weekStartsOn: 1 });
      }

      loadLessonsQuery(
        {
          endDate: endDate?.toString(),
          orderBy: "rearrangedTimestamp,scheduledTimestamp",
          profileId: user.profile.id,
          skip: false,
          startDate: startDate.toString(),
        },
        { fetchPolicy: "store-or-network" }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadLessonsQuery, user?.profile?.id]);

  const toast = useToast();

  const commitRescheduleLesson = useMutation<RescheduleLessonMutation>(reschedule_lesson)[0];

  const rescheduleLesson = useCallback(
    (lessonId: string, rearrangedDatetime: Date, lessonDuration?: number) => {
      const rescheduleLessonConfig = {
        variables: {
          input: {
            lessonDuration: lessonDuration,
            lessonId: lessonId,
            rearrangedDatetime: String(rearrangedDatetime),
          },
        },
        onCompleted: (response: RescheduleLessonMutation$data) => {
          if (response?.rescheduleLesson?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="success" title={"Rescheduled lesson"} toast={toast} />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't reschedule lesson"}
                  toast={toast}
                />
              ),
            });
          }
        },
        optimsticResponse: {
          rescheduleLesson: {
            errors: null,
            lesson: {
              id: lessonId,
              rearrangedTimestamp: rearrangedDatetime,
            },
            success: true,
          },
        },
      };

      commitRescheduleLesson(rescheduleLessonConfig);
    },
    [commitRescheduleLesson, toast]
  );

  const { height: windowHeight, scale } = useWindowDimensions();

  if (!user?.profile?.id) {
    return null;
  }

  return (
    <Box bg="surface.100" flex={1} pt="70">
      <Box flex={1} mx="30px" pt="6">
        <HStack
          mb={getScaledWindowDimension(windowHeight, scale) > 1000 ? "4" : "-8"}
          ml="1"
          space="3"
        >
          <TimetableIcon color="primary.600" size="2xl" />
          <Heading color="primary.600" fontSize="xl">
            Timetable
          </Heading>
        </HStack>
        <TeacherTimetable
          dispatchState={dispatchState}
          loadLessonsQuery={loadLessonsQuery}
          loadLessonsQueryReference={loadLessonsQueryReference}
          navigation={navigation}
          profileId={user.profile.id}
          state={state}
          userIsTeacher
          viewToggleOnRight
        />
      </Box>
      <DateTimePickerModal
        accentColor={customColors.primary[800]}
        animationType="fade"
        canChooseEndTime
        date={state.values.dateTimePickerModalDate}
        duration={state.values.dateTimePickerModalLessonDuration}
        hours={state.values.dateTimePickerModalDate?.getHours()}
        label="Reschedule Lesson"
        locale="en"
        minutes={state.values.dateTimePickerModalDate?.getMinutes()}
        onConfirm={(params) => {
          if (state.values.dateTimePickerModalLessonId && params.date) {
            rescheduleLesson(
              state.values.dateTimePickerModalLessonId,
              params.date,
              params.duration
            );
          }
          dispatchState({
            input: "dateTimePickerModalDate",
            value: undefined,
          });
          dispatchState({
            input: "dateTimePickerModalLessonId",
            value: undefined,
          });
        }}
        onDismiss={() =>
          dispatchState({
            input: "dateTimePickerModalDate",
            value: undefined,
          })
        }
        saveLabel="Save"
        startWeekOnMonday
        textStyle={{
          fontFamily: "Poppins-SemiBold",
        }}
        uppercase={false}
        visible={state.values.dateTimePickerModalDate !== undefined}
      />
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Timetable",
};

export default TeacherTimetableHubScreen;
